import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { mockImpedimentGroups } from '../../mock-data/impediment.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiImpediment } from '../api-types/impediment.api';
import { Impediment, ImpedimentGroup } from '../app-types/impediment.app';
import {
	transformApiImpedimentsToAppImpedimentGroupsWithImpediments,
	transformApiImpedimentToAppImpediment,
} from '../transformers/impediment.transform';
import { BaseRequestService } from './base-request.service';
import { ENVIRONMENT } from '../../../environments/environment.interface';

export interface ImpedimentProvider {
	getPossibleImpediments(
		assignmentId: string | number,
		categoryReportId: string | number,
		offset?: number,
		range?: number,
	): Promise<ImpedimentGroup[]>;
	saveImpedimentsToReport(reportId: string | number, impedimentIds: number[]): Promise<void>;
	getImpedimentsOfReport(reportId: string | number): Promise<PaginatedResponse<Impediment>>;
	getAllImpediments(assignmentId: string | number, categoryReportId: string | number): Promise<ImpedimentGroup[]>;
}

export const IMPEDIMENT_PROVIDER = new InjectionToken<ImpedimentProvider>('IMPEDIMENT_PROVIDER');

@Injectable()
export class ImpedimentService extends BaseRequestService implements ImpedimentProvider {
	public async getPossibleImpediments(
		assignmentId: string | number,
		categoryReportId: string | number,
		offset?: number,
		range?: number,
	) {
		const response = await this.get<PaginatedResponse<ApiImpediment>>(
			`assignmentimpediments/assignments/${assignmentId}/categoryReport/${categoryReportId}`,
			{
				offset,
				range,
			},
		);
		const entities = transformApiImpedimentsToAppImpedimentGroupsWithImpediments(response.entities || []);
		return entities;
	}

	public async saveImpedimentsToReport(reportId: string | number, impedimentIds: number[]) {
		const body = impedimentIds.map((id) => ({ impedimentId: id }));
		await this.post(`assignmentimpediments/report/${reportId}`, body);
	}

	public async getImpedimentsOfReport(reportId: string | number) {
		const response = await this.get<PaginatedResponse<ApiImpediment>>(`assignmentimpediments/report/${reportId}`);
		const entities = (response.entities || []).map(transformApiImpedimentToAppImpediment);
		const page: PaginatedResponse<Impediment> = {
			entities,
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
		return page;
	}

	/**
	 * Warning: Only use this function if you really need all impediments at once!
	 * This method contains a recursive pagination to get all impediments from the server.
	 */
	public async getAllImpediments(assignmentId: string | number, categoryReportId: string | number) {
		let allImpediments: ApiImpediment[] = [];
		let offset = 0;
		const range = 100;

		while (true) {
			const response = await this.get<PaginatedResponse<ApiImpediment>>(
				`assignmentimpediments/assignments/${assignmentId}/categoryReport/${categoryReportId}`,
				{
					offset,
					range,
				},
			);
			const entities = response.entities || [];
			allImpediments = allImpediments.concat(entities);

			if (allImpediments.length >= (response.totalEntities || 0)) {
				break;
			}
			offset += 1;
		}

		return transformApiImpedimentsToAppImpedimentGroupsWithImpediments(allImpediments);
	}
}

@Injectable()
export class MockImpedimentService implements ImpedimentProvider {
	private readonly env = inject(ENVIRONMENT);

	public constructor() {
		if (this.env.showDevTools) {
			console.warn('⛔️⛔️⛔️ Impediment Mock Service: You are currently using a mock service!');
		}
	}

	public async getPossibleImpediments(
		_assignmentId: string | number,
		_categoryReportId: string | number,
		offset?: number,
		range?: number,
	) {
		const res: PaginatedResponse<ImpedimentGroup> = {
			entities: mockImpedimentGroups,
			totalEntities: mockImpedimentGroups.length,
			offset: offset || 0,
			range: range || mockImpedimentGroups.length,
		};
		return res.entities;
	}

	public async saveImpedimentsToReport(reportId: string | number, impedimentIds: number[]) {
		const body = impedimentIds.map((id) => ({ impedimentId: id }));
		if (this.env.showDevTools) {
			console.log(`Mock saving impediments to report ${reportId}:`, body);
		}
		return Promise.resolve();
	}

	public async getImpedimentsOfReport(_reportId: string | number) {
		return {
			entities: mockImpedimentGroups,
			totalEntities: mockImpedimentGroups.length,
			offset: 0,
			range: mockImpedimentGroups.length,
		};
	}

	public async getAllImpediments(_assignmentId: string | number, _categoryReportId: string | number) {
		return mockImpedimentGroups;
	}
}

export const provideImpedimentApi: Provider = {
	provide: IMPEDIMENT_PROVIDER,
	useClass: ImpedimentService,
};
